import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Prompt, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Image,
  Input,
  List,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  CameraOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  MailOutlined,
  MoreOutlined,
  PlusOutlined,
  SaveOutlined,
  SendOutlined,
  ShareAltOutlined,
  StopOutlined,
  UnlockOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success, Error as ErrorMessage } from "../../../commons/Messages/Messages";
import {
  createSignLink,
  deletePhoto,
  deleteReport,
  emailReport,
  getMeta,
  getReport,
  invoiceReports,
  pdfUrl,
  photoUrl,
  proposeLabour,
  refuseReport,
  reopenReport,
  revokeSignLink,
  signReport,
  updateReport,
  uploadPhoto
} from "./api";
import { normalizeLabour, normalizeMaterial, summarizeReport } from "./calc";
import {
  STATUS_COLORS,
  formatDateTime,
  formatDay,
  formatEuro,
  formatMinutes,
  statusLabels
} from "./format";
import { LabourLine, MaterialLine } from "./LineEditors";
import ReportPreview from "./ReportPreview";
import SignDialog from "./SignDialog";
import { shrinkPhoto } from "./photos";
import OfflineNotice from "../../../offline/OfflineNotice";
import { useSyncedRecord } from "../../../offline/hooks";
import "./workReports.css";

const DRAFT_PREFIX = "work-report-draft:";
const FIELDS = [
  "date",
  "project",
  "client",
  "siteAddress",
  "orderReference",
  "changeOrder",
  "workDescription",
  "internalNote",
  "showPrices",
  "taxRate",
  "labour",
  "materials"
];

let keySeed = 0;
const newKey = () => `l${Date.now().toString(36)}${(keySeed += 1)}`;
const withKey = line => ({ ...line, key: line.key || line._id || newKey() });

const readDraft = id => {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_PREFIX + id) || "null");
  } catch {
    return null;
  }
};
const writeDraft = (id, draft) => {
  try {
    if (draft) localStorage.setItem(DRAFT_PREFIX + id, JSON.stringify(draft));
    else localStorage.removeItem(DRAFT_PREFIX + id);
  } catch {
    // storage full or blocked: the server copy is still there
  }
};

const formFromReport = report => ({
  date: report.date || "",
  project: report.project || null,
  client: report.client || null,
  siteAddress: report.siteAddress || "",
  orderReference: report.orderReference || "",
  changeOrder: report.changeOrder || null,
  workDescription: report.workDescription || "",
  internalNote: report.internalNote || "",
  showPrices: report.showPrices !== false,
  taxRate: report.taxRate ?? 19,
  labour: (report.labour || []).map(withKey),
  materials: (report.materials || []).map(withKey),
  photoCaptions: {}
});

// The client-only list key is not part of a stored line
const withoutKey = line => {
  const copy = { ...line };
  delete copy.key;
  return copy;
};

const bodyFromForm = form => {
  const body = {};
  FIELDS.forEach(key => {
    body[key] = form[key];
  });
  body.labour = form.labour.map(withoutKey);
  body.materials = form.materials.map(withoutKey);
  body.photoCaptions = form.photoCaptions;
  return body;
};

const WorkReportEditor = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const { id } = useParams();
  const history = useHistory();
  const fileInput = useRef(null);
  const [meta, setMeta] = useState(null);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [uploading, setUploading] = useState(0);
  const [signOpen, setSignOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // refuse | reopen | link | email | linkCreated
  const [dialogValue, setDialogValue] = useState("");
  const [dialogSend, setDialogSend] = useState(true);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  // Created offline: moves to the real address once uploaded
  useSyncedRecord(id, serverId => history.replace(`/user/work-reports/${serverId}`), {
    when: !dirty
  });

  const load = async () => {
    const [metaRes, reportRes] = await Promise.all([getMeta(), getReport(id)]);
    setMeta(metaRes.data);
    applyReport(reportRes.data.data);
    const local = readDraft(id);
    if (
      local &&
      reportRes.data.data.status === "draft" &&
      local.savedAt > Date.parse(reportRes.data.data.updatedAt)
    ) {
      setDraft(local);
    } else if (local) {
      writeDraft(id, null);
    }
  };

  const applyReport = data => {
    setReport(data);
    setForm(formFromReport(data));
    setDirty(false);
  };

  useEffect(() => {
    load().catch(error => {
      generalErrorAlert(error);
      history.push("/user/work-reports");
    });
  }, [id]);

  // Unsaved edits are kept on the device until saved (no signal on many sites)
  useEffect(() => {
    if (dirty && form) writeDraft(id, { form, savedAt: Date.now() });
  }, [dirty, form]);

  const settings = meta?.settings || {};
  const readOnly = !report || report.status !== "draft" || !meta?.can.update;
  const summary = useMemo(
    () => (form ? summarizeReport({ ...form, rounding: report?.rounding }, settings) : null),
    [form, report?.rounding, settings]
  );

  const update = patch => {
    setForm(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const save = async ({ quiet = false } = {}) => {
    setSaving(true);
    try {
      const res = await updateReport(id, bodyFromForm(form));
      applyReport(res.data.data);
      writeDraft(id, null);
      if (!quiet) Success(t("Saved"));
      return res.data.data;
    } catch (error) {
      generalErrorAlert(error);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveIfDirty = async () => (dirty ? save({ quiet: true }) : report);

  // ---- Lines ----
  const setLine = (list, key, next) =>
    update({ [list]: form[list].map(line => (line.key === key ? { ...next, key } : line)) });
  const removeLine = (list, key) => update({ [list]: form[list].filter(line => line.key !== key) });
  const addLabour = () =>
    update({
      labour: [
        ...form.labour,
        withKey(normalizeLabour({ kind: "WORK", rate: settings.hourlyRate }))
      ]
    });
  const addMaterial = kind =>
    update({
      materials: [
        ...form.materials,
        withKey(
          normalizeMaterial(
            kind === "FLAT"
              ? { kind, description: t("Call-out charge"), quantity: 1, unit: "psch" }
              : { kind, quantity: 1, unit: "Stk" }
          )
        )
      ]
    });

  const importAttendance = async () => {
    try {
      const res = await proposeLabour(id, { date: form.date, project: form.project });
      const { lines, running } = res.data;
      if (!lines.length) {
        Modal.info({
          title: t("No working times found"),
          content: running.length
            ? t("Still running for: {{names}}. End the time first.", { names: running.join(", ") })
            : t("Nobody recorded time on this project on this day.")
        });
        return;
      }
      Modal.confirm({
        title: t("Take over the recorded times?"),
        width: 560,
        content: (
          <div>
            <ul className="wr-import-list">
              {lines.map((line, index) => (
                <li key={index}>
                  <strong>{line.name}</strong> · {line.kind === "TRAVEL" ? t("Travel") : t("Work")}{" "}
                  {line.start && line.end ? `${line.start}–${line.end}` : ""}{" "}
                  {formatMinutes(line.minutes)} h
                </li>
              ))}
            </ul>
            {running.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={t("Still running for: {{names}}. End the time first.", {
                  names: running.join(", ")
                })}
              />
            )}
            <Typography.Paragraph type="secondary" className="wr-import-note">
              {t("Lines taken over earlier and empty lines of these people are replaced.")}
            </Typography.Paragraph>
          </div>
        ),
        okText: t("Take over"),
        cancelText: t("Cancel"),
        onOk: () => {
          const people = new Set(lines.map(line => String(line.employee)));
          const kept = form.labour.filter(
            line =>
              line.source !== "ATTENDANCE" &&
              !(line.employee && people.has(String(line.employee)) && !line.minutes && !line.start)
          );
          update({ labour: [...kept, ...lines.map(withKey)] });
        }
      });
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  // ---- Photos ----
  const addPhotos = async files => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (dirty && !(await save({ quiet: true }))) return;
    setUploading(list.length);
    let latest = null;
    for (const file of list) {
      try {
        const { blob, width, height } = await shrinkPhoto(file);
        const res = await uploadPhoto(id, blob, { width, height });
        latest = res.data.data;
      } catch (error) {
        generalErrorAlert(error);
      } finally {
        setUploading(n => Math.max(0, n - 1));
      }
    }
    if (latest) applyReport(latest);
  };

  const removePhoto = photoId =>
    Modal.confirm({
      title: t("Remove this photo?"),
      okText: t("Remove"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          if (dirty && !(await save({ quiet: true }))) return;
          const res = await deletePhoto(id, photoId);
          applyReport(res.data.data);
        } catch (error) {
          generalErrorAlert(error);
        }
      }
    });

  // ---- Signing and the other actions ----
  const checkBeforeSigning = () => {
    if (!form.workDescription.trim()) {
      ErrorMessage(t("Please describe the work carried out before signing."));
      return false;
    }
    if (form.materials.some(line => !String(line.description || "").trim())) {
      ErrorMessage(t("Every material line needs a description."));
      return false;
    }
    if (!form.labour.length && !form.materials.length) {
      ErrorMessage(t("Add at least one time or material line before signing."));
      return false;
    }
    return true;
  };

  const startSigning = async () => {
    if (!checkBeforeSigning()) return;
    if (!(await saveIfDirty())) return;
    setSignOpen(true);
  };

  const sign = async values => {
    try {
      const res = await signReport(id, values);
      applyReport(res.data.data);
      setSignOpen(false);
      Success(t("Signed. The report can no longer be changed."));
      if (res.data.emailedTo)
        Success(t("A copy was sent to {{email}}.", { email: res.data.emailedTo }));
      if ((res.data.warnings || []).includes("EMAIL_FAILED")) {
        ErrorMessage(t("The copy could not be emailed. Send it again from the menu."));
      }
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const openDialog = async kind => {
    if (["refuse", "link"].includes(kind)) {
      if (!checkBeforeSigning()) return;
      if (!(await saveIfDirty())) return;
    }
    setDialogValue(kind === "email" || kind === "link" ? report.clientInfo?.email || "" : "");
    setDialogSend(true);
    setDialog(kind);
  };

  const runDialog = async () => {
    setDialogBusy(true);
    try {
      if (dialog === "refuse") {
        const res = await refuseReport(id, { reason: dialogValue });
        applyReport(res.data.data);
        Success(t("Saved: the customer did not sign."));
        setDialog(null);
      } else if (dialog === "reopen") {
        const res = await reopenReport(id, { reason: dialogValue });
        applyReport(res.data.data);
        Success(t("Reopened. The customer has to sign again."));
        setDialog(null);
      } else if (dialog === "email") {
        const res = await emailReport(id, { to: dialogValue });
        Success(t("Sent to {{email}}.", { email: res.data.sentTo }));
        setDialog(null);
      } else if (dialog === "link") {
        const res = await createSignLink(id, { email: dialogValue, send: dialogSend });
        applyReport(res.data.data);
        setCreatedLink(res.data);
        setDialog("linkCreated");
      }
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setDialogBusy(false);
    }
  };

  const shareLink = async () => {
    const text = t("Please check and sign work report {{number}}.", { number: report.number });
    if (navigator.share) {
      try {
        await navigator.share({ title: report.number, text, url: createdLink.url });
        return;
      } catch {
        // cancelled: fall through to copy
      }
    }
    await navigator.clipboard?.writeText(createdLink.url);
    Success(t("Link copied"));
  };

  const revokeLink = async () => {
    try {
      const res = await revokeSignLink(id);
      applyReport(res.data.data);
      Success(t("The link no longer works."));
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const createInvoice = async () => {
    try {
      const res = await invoiceReports([id]);
      Success(t("Draft invoice created. Check it and send it from Invoices."));
      history.push("/user/invoices", { highlight: res.data.invoice._id });
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const remove = () =>
    Modal.confirm({
      title: t("Delete this work report?"),
      okText: t("Delete"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await deleteReport(id);
          writeDraft(id, null);
          setDirty(false);
          history.push("/user/work-reports");
        } catch (error) {
          generalErrorAlert(error);
        }
      }
    });

  if (!form || !meta || !summary) {
    return (
      <div className="work-reports-page">
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );
  }

  const draftActions = [
    {
      key: "link",
      icon: <SendOutlined />,
      label: t("Send signing link"),
      onClick: () => openDialog("link")
    },
    {
      key: "refuse",
      icon: <StopOutlined />,
      label: t("Customer did not sign"),
      onClick: () => openDialog("refuse")
    },
    {
      key: "pdf",
      icon: <FilePdfOutlined />,
      label: t("Preview PDF"),
      onClick: () => window.open(pdfUrl(id, lng), "_blank")
    },
    ...(meta.can.delete
      ? [
          {
            key: "delete",
            icon: <DeleteOutlined />,
            danger: true,
            label: t("Delete"),
            onClick: remove
          }
        ]
      : [])
  ];

  const customerName =
    meta.clients.find(c => c._id === form.client)?.name || report.clientInfo?.name;
  const projectName =
    meta.projects.find(p => p._id === form.project)?.name || report.projectInfo?.name;
  const previewView = {
    ...summary,
    number: report.number,
    date: form.date,
    siteAddress: form.siteAddress,
    orderReference: form.orderReference,
    workDescription: form.workDescription,
    showPrices: form.showPrices,
    terms: settings.terms,
    customer: customerName ? { name: customerName } : null,
    project: projectName ? { name: projectName } : null,
    photos: report.photos,
    signature: report.signature?.signedAt ? report.signature : null,
    refusal: report.refusal?.at ? report.refusal : null
  };
  const warningText = {
    NO_TIME: t("A time line has no duration."),
    NO_RATE: t("A time line has no hourly rate."),
    NO_NAME: t("A time line has no name."),
    OVERLAP: t("Times of the same person overlap."),
    NO_DESCRIPTION: t("A material line has no description."),
    NO_QUANTITY: t("A material line has no quantity."),
    EMPTY: t("No times or materials yet.")
  };
  const warnings = [...new Set(summary.warnings.map(w => w.code))];
  const historyLabels = {
    create: t("Created"),
    sign: t("Signed"),
    refuse: t("Signature refused"),
    reopen: t("Reopened"),
    link: t("Signing link created"),
    email: t("Emailed"),
    invoice: t("Invoiced"),
    uninvoice: t("Invoice removed"),
    remote_view: t("Opened by the customer")
  };

  return (
    <div className="work-reports-page">
      <Prompt when={dirty && !saving} message={t("You have unsaved changes. Leave anyway?")} />
      <OfflineNotice module="work-reports" record={report} />

      {draft && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={t("Unsaved changes from {{time}} were found on this device.", {
            time: dayjs(draft.savedAt).format("DD.MM.YYYY HH:mm")
          })}
          action={
            <Space direction="vertical" size={4}>
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  setForm(draft.form);
                  setDirty(true);
                  setDraft(null);
                }}
              >
                {t("Restore")}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  writeDraft(id, null);
                  setDraft(null);
                }}
              >
                {t("Discard")}
              </Button>
            </Space>
          }
        />
      )}

      <Card className="mb-3">
        <div className="wr-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label={t("Back")}
            onClick={() => history.push("/user/work-reports")}
          />
          <div className="wr-header-title">
            <Typography.Title level={4}>
              {t("Work report")} {report.number}
            </Typography.Title>
            <Tag color={STATUS_COLORS[report.status]}>{statusLabels(t)[report.status]}</Tag>
          </div>
          <Space
            wrap
            className={`wr-header-actions${report.status === "draft" ? " wr-hide-phone" : ""}`}
          >
            {report.status === "draft" && meta.can.update && (
              <>
                <Button
                  icon={<SaveOutlined />}
                  loading={saving}
                  disabled={!dirty}
                  onClick={() => save()}
                >
                  {t("Save")}
                </Button>
                <Button type="primary" icon={<EditOutlined />} onClick={startSigning}>
                  {t("Sign now")}
                </Button>
                <Dropdown menu={{ items: draftActions }} trigger={["click"]}>
                  <Button icon={<MoreOutlined />} aria-label={t("More")} />
                </Dropdown>
              </>
            )}
            {report.status !== "draft" && (
              <>
                <Button
                  icon={<FilePdfOutlined />}
                  onClick={() => window.open(pdfUrl(id, lng), "_blank")}
                >
                  PDF
                </Button>
                <Button icon={<MailOutlined />} onClick={() => openDialog("email")}>
                  {t("Email")}
                </Button>
                {["signed", "refused"].includes(report.status) && meta.can.invoice && (
                  <Button type="primary" icon={<FileTextOutlined />} onClick={createInvoice}>
                    {t("Create invoice")}
                  </Button>
                )}
                {["signed", "refused"].includes(report.status) && meta.can.update && (
                  <Button icon={<UnlockOutlined />} onClick={() => openDialog("reopen")}>
                    {t("Reopen")}
                  </Button>
                )}
              </>
            )}
          </Space>
        </div>

        {report.status === "invoiced" && report.invoiceInfo && (
          <Alert
            type="info"
            showIcon
            className="mt-2"
            message={t("Invoiced with {{invoice}}.", {
              invoice: report.invoiceInfo.invoiceNumber || report.invoiceInfo.title
            })}
            action={<Link to="/user/invoices">{t("Invoices")}</Link>}
          />
        )}
        {report.status === "draft" && report.signLink?.active && (
          <Alert
            type="info"
            showIcon
            className="mt-2"
            message={
              report.signLink.sentTo
                ? t("Signing link sent to {{email}}, valid until {{date}}.", {
                    email: report.signLink.sentTo,
                    date: formatDateTime(report.signLink.expiresAt, lng)
                  })
                : t("A signing link is active until {{date}}.", {
                    date: formatDateTime(report.signLink.expiresAt, lng)
                  })
            }
            action={
              meta.can.update && (
                <Button size="small" onClick={revokeLink}>
                  {t("Revoke")}
                </Button>
              )
            }
          />
        )}
      </Card>

      {report.status !== "draft" ? (
        <Card className="mb-3">
          <ReportPreview view={previewView} photoSrc={photoId => photoUrl(id, photoId)} />
          {report.internalNote && (
            <Alert
              className="mt-3"
              type="warning"
              message={t("Internal note")}
              description={report.internalNote}
            />
          )}
        </Card>
      ) : (
        <>
          <Card className="mb-3" title={t("Order and site")}>
            <Form layout="vertical">
              <Row gutter={[12, 0]}>
                <Col xs={12} md={6}>
                  <Form.Item label={t("Date")} required>
                    <DatePicker
                      className="wr-full"
                      format="DD.MM.YYYY"
                      allowClear={false}
                      value={form.date ? dayjs(form.date) : null}
                      disabled={readOnly}
                      onChange={value =>
                        update({ date: value ? value.format("YYYY-MM-DD") : form.date })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label={t("Order / Nachtrag")}>
                    <Input
                      value={form.orderReference}
                      disabled={readOnly}
                      maxLength={200}
                      onChange={event => update({ orderReference: event.target.value })}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("Project")}>
                    <Select
                      className="wr-full"
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      value={form.project || undefined}
                      disabled={readOnly}
                      options={meta.projects.map(p => ({ value: p._id, label: p.name }))}
                      onChange={value => {
                        const project = meta.projects.find(p => p._id === value);
                        update({
                          project: value || null,
                          ...(project?.client && !form.client ? { client: project.client } : {})
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                {(meta.changeOrders || []).length > 0 && (
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t("Change order")}
                      extra={t("Work done under a change order (e.g. billed by the hour).")}
                    >
                      <Select
                        className="wr-full"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        value={form.changeOrder || undefined}
                        disabled={readOnly}
                        options={meta.changeOrders
                          .filter(c => !form.project || !c.project || c.project === form.project)
                          .map(c => ({ value: c._id, label: c.label }))}
                        onChange={value => {
                          const changeOrder = meta.changeOrders.find(c => c._id === value);
                          update({
                            changeOrder: value || null,
                            ...(changeOrder && !form.orderReference
                              ? { orderReference: changeOrder.label.slice(0, 200) }
                              : {}),
                            ...(changeOrder?.project && !form.project
                              ? { project: changeOrder.project }
                              : {}),
                            ...(changeOrder?.client && !form.client
                              ? { client: changeOrder.client }
                              : {})
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} md={12}>
                  <Form.Item label={t("Customer")}>
                    <Select
                      className="wr-full"
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      value={form.client || undefined}
                      disabled={readOnly}
                      options={meta.clients.map(c => ({ value: c._id, label: c.name }))}
                      onChange={value => {
                        const client = meta.clients.find(c => c._id === value);
                        update({
                          client: value || null,
                          ...(client?.address && !form.siteAddress
                            ? { siteAddress: client.address }
                            : {})
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("Site address")}>
                    <Input
                      value={form.siteAddress}
                      disabled={readOnly}
                      maxLength={500}
                      autoComplete="street-address"
                      onChange={event => update({ siteAddress: event.target.value })}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label={t("Work carried out")} required className="mb-0">
                <Input.TextArea
                  value={form.workDescription}
                  disabled={readOnly}
                  autoSize={{ minRows: 3, maxRows: 12 }}
                  maxLength={10000}
                  placeholder={t("What was done, what was found, what is still open …")}
                  onChange={event => update({ workDescription: event.target.value })}
                />
              </Form.Item>
            </Form>
          </Card>

          <Card
            className="mb-3 wr-section"
            title={`${t("Working times")} (${form.labour.length})`}
            extra={
              !readOnly && (
                <Space wrap>
                  <Button icon={<ClockCircleOutlined />} onClick={importAttendance}>
                    {t("From time tracking")}
                  </Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={addLabour}>
                    {t("Add time")}
                  </Button>
                </Space>
              )
            }
          >
            {form.labour.length ? (
              form.labour.map((line, index) => (
                <LabourLine
                  key={line.key}
                  line={line}
                  computed={summary.labour[index]}
                  employees={meta.employees}
                  readOnly={readOnly}
                  lng={lng}
                  onChange={next => setLine("labour", line.key, next)}
                  onRemove={() => removeLine("labour", line.key)}
                />
              ))
            ) : (
              <Empty
                description={t("No times yet. Add them or take them over from time tracking.")}
              />
            )}
          </Card>

          <Card
            className="mb-3 wr-section"
            title={`${t("Materials and flat rates")} (${form.materials.length})`}
            extra={
              !readOnly && (
                <Space wrap>
                  <Button onClick={() => addMaterial("FLAT")}>{t("Flat rate")}</Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => addMaterial("MATERIAL")}
                  >
                    {t("Add material")}
                  </Button>
                </Space>
              )
            }
          >
            {form.materials.length ? (
              form.materials.map((line, index) => (
                <MaterialLine
                  key={line.key}
                  line={line}
                  computed={summary.materials[index]}
                  readOnly={readOnly}
                  lng={lng}
                  onChange={next => setLine("materials", line.key, next)}
                  onRemove={() => removeLine("materials", line.key)}
                />
              ))
            ) : (
              <Empty description={t("No materials used.")} />
            )}
          </Card>

          <Card
            className="mb-3 wr-section"
            title={`${t("Photos")} (${report.photos.length})`}
            extra={
              !readOnly && (
                <Button
                  icon={<CameraOutlined />}
                  loading={uploading > 0}
                  onClick={() => fileInput.current?.click()}
                >
                  {uploading > 0 ? t("Uploading {{n}} …", { n: uploading }) : t("Add photos")}
                </Button>
              )
            }
          >
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              hidden
              onChange={event => {
                addPhotos(event.target.files);
                event.target.value = "";
              }}
            />
            {report.photos.length ? (
              <div className="wr-photo-grid">
                <Image.PreviewGroup>
                  {report.photos.map(photo => (
                    <figure key={photo._id}>
                      <Image src={photoUrl(id, photo._id)} alt={photo.caption || ""} />
                      <Input
                        size="small"
                        placeholder={t("Caption")}
                        disabled={readOnly}
                        maxLength={300}
                        value={form.photoCaptions[photo._id] ?? photo.caption}
                        onChange={event =>
                          update({
                            photoCaptions: {
                              ...form.photoCaptions,
                              [photo._id]: event.target.value
                            }
                          })
                        }
                      />
                      {!readOnly && (
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label={t("Remove")}
                          className="wr-photo-remove"
                          onClick={() => removePhoto(photo._id)}
                        />
                      )}
                    </figure>
                  ))}
                </Image.PreviewGroup>
              </div>
            ) : (
              <Empty
                description={t("Before/after pictures and findings make a report hard to dispute.")}
              />
            )}
          </Card>

          <Card className="mb-3" title={t("Totals and options")}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <dl className="wr-totals">
                  <dt>{t("Working time")}</dt>
                  <dd>{formatMinutes(summary.totals.workMinutes)} h</dd>
                  <dt>{t("Travel time")}</dt>
                  <dd>{formatMinutes(summary.totals.travelMinutes)} h</dd>
                  {summary.rounding > 0 && (
                    <>
                      <dt>{t("Billed (per started {{n}} min)", { n: summary.rounding })}</dt>
                      <dd>{formatMinutes(summary.totals.billedMinutes)} h</dd>
                    </>
                  )}
                  <dt>{t("Labour")}</dt>
                  <dd>{formatEuro(summary.totals.labourNet, lng)}</dd>
                  <dt>{t("Materials")}</dt>
                  <dd>{formatEuro(summary.totals.materialNet, lng)}</dd>
                  <dt>{t("Net")}</dt>
                  <dd>{formatEuro(summary.totals.net, lng)}</dd>
                  <dt>
                    {t("VAT")} {summary.taxRate} %
                  </dt>
                  <dd>{formatEuro(summary.totals.tax, lng)}</dd>
                  <dt className="wr-gross">{t("Gross")}</dt>
                  <dd className="wr-gross">{formatEuro(summary.totals.gross, lng)}</dd>
                </dl>
              </Col>
              <Col xs={24} md={12}>
                <Form layout="vertical">
                  <Form.Item label={t("Show prices to the customer")} className="mb-2">
                    <Switch
                      checked={form.showPrices}
                      disabled={readOnly}
                      onChange={showPrices => update({ showPrices })}
                    />
                  </Form.Item>
                  <Form.Item label={t("VAT rate")} className="mb-2">
                    <Select
                      className="wr-full"
                      value={form.taxRate}
                      disabled={readOnly}
                      options={[19, 7, 0].map(rate => ({ value: rate, label: `${rate} %` }))}
                      onChange={taxRate => update({ taxRate })}
                    />
                  </Form.Item>
                  <Form.Item
                    label={t("Internal note (not shown to the customer)")}
                    className="mb-0"
                  >
                    <Input.TextArea
                      value={form.internalNote}
                      disabled={readOnly}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      maxLength={5000}
                      onChange={event => update({ internalNote: event.target.value })}
                    />
                  </Form.Item>
                </Form>
              </Col>
            </Row>
            {warnings.length > 0 && (
              <Alert
                className="mt-3"
                type="warning"
                showIcon
                message={t("Please check")}
                description={
                  <ul className="wr-warnings">
                    {warnings.map(code => (
                      <li key={code}>{warningText[code]}</li>
                    ))}
                  </ul>
                }
              />
            )}
          </Card>

          {!readOnly && (
            <div className="wr-bottom-bar">
              <Button
                size="large"
                icon={<SaveOutlined />}
                loading={saving}
                disabled={!dirty}
                onClick={() => save()}
              >
                {t("Save")}
              </Button>
              <Button size="large" type="primary" icon={<EditOutlined />} onClick={startSigning}>
                {t("Sign now")}
              </Button>
              <Dropdown menu={{ items: draftActions }} trigger={["click"]} placement="topRight">
                <Button size="large" icon={<MoreOutlined />} aria-label={t("More")} />
              </Dropdown>
            </div>
          )}
        </>
      )}

      {(report.history || []).length > 0 && (
        <Card className="mb-3" size="small" title={t("History")}>
          <List
            size="small"
            dataSource={[...report.history].reverse()}
            renderItem={entry => (
              <List.Item>
                <Typography.Text type="secondary" className="wr-num">
                  {formatDateTime(entry.at, lng)}
                </Typography.Text>{" "}
                {historyLabels[entry.action] || entry.action}
                {entry.note && !/^[a-f0-9]{24}$/.test(entry.note) ? ` · ${entry.note}` : ""}
              </List.Item>
            )}
          />
        </Card>
      )}

      <SignDialog
        open={signOpen}
        view={previewView}
        photoSrc={photoId => photoUrl(id, photoId)}
        defaultName=""
        defaultEmail={settings.emailCopy ? report.clientInfo?.email || "" : ""}
        onCancel={() => setSignOpen(false)}
        onSign={sign}
      />

      <Modal
        rootClassName="work-reports-page"
        open={["refuse", "reopen", "email", "link"].includes(dialog)}
        title={
          {
            refuse: t("Customer did not sign"),
            reopen: t("Reopen the report"),
            email: t("Email the report"),
            link: t("Send signing link")
          }[dialog]
        }
        okText={
          {
            refuse: t("Save"),
            reopen: t("Reopen"),
            email: t("Send"),
            link: dialogSend ? t("Send") : t("Create link")
          }[dialog]
        }
        cancelText={t("Cancel")}
        confirmLoading={dialogBusy}
        onOk={runDialog}
        onCancel={() => setDialog(null)}
        destroyOnClose
      >
        {dialog === "refuse" && (
          <>
            <Typography.Paragraph type="secondary">
              {t("The report is locked and stays billable. Note why there is no signature.")}
            </Typography.Paragraph>
            <Input.TextArea
              autoFocus
              value={dialogValue}
              maxLength={1000}
              autoSize={{ minRows: 3 }}
              placeholder={t("e.g. customer not present, refused to sign")}
              onChange={event => setDialogValue(event.target.value)}
            />
          </>
        )}
        {dialog === "reopen" && (
          <>
            <Typography.Paragraph type="secondary">
              {t(
                "The signature is removed and kept in the history. The customer has to sign again."
              )}
            </Typography.Paragraph>
            <Input.TextArea
              autoFocus
              value={dialogValue}
              maxLength={1000}
              autoSize={{ minRows: 2 }}
              placeholder={t("Reason")}
              onChange={event => setDialogValue(event.target.value)}
            />
          </>
        )}
        {dialog === "email" && (
          <Input
            type="email"
            inputMode="email"
            autoFocus
            value={dialogValue}
            placeholder={t("Email address")}
            onChange={event => setDialogValue(event.target.value)}
          />
        )}
        {dialog === "link" && (
          <Space direction="vertical" className="wr-full">
            <Typography.Paragraph type="secondary">
              {t(
                "The customer opens the report on their phone or computer and signs there. The link is valid for 14 days."
              )}
            </Typography.Paragraph>
            <Switch
              checked={dialogSend}
              onChange={setDialogSend}
              checkedChildren={t("Send by email")}
              unCheckedChildren={t("Only create the link")}
            />
            {dialogSend && (
              <Input
                type="email"
                inputMode="email"
                value={dialogValue}
                placeholder={t("Email address")}
                onChange={event => setDialogValue(event.target.value)}
              />
            )}
          </Space>
        )}
      </Modal>

      <Modal
        rootClassName="work-reports-page"
        open={dialog === "linkCreated"}
        title={t("Signing link")}
        onCancel={() => setDialog(null)}
        footer={[
          <Button key="share" icon={<ShareAltOutlined />} onClick={shareLink}>
            {t("Share")}
          </Button>,
          <Button key="ok" type="primary" onClick={() => setDialog(null)}>
            {t("Done")}
          </Button>
        ]}
      >
        {createdLink?.sentTo && (
          <Alert
            type="success"
            showIcon
            className="mb-3"
            message={t("Sent to {{email}}.", { email: createdLink.sentTo })}
          />
        )}
        <Typography.Paragraph type="secondary">
          {t("You can also send the link by WhatsApp or SMS. It is shown only now.")}
        </Typography.Paragraph>
        <Space.Compact className="wr-link-group">
          <Input readOnly value={createdLink?.url} />
          <Button
            icon={<CopyOutlined />}
            aria-label={t("Copy")}
            onClick={async () => {
              await navigator.clipboard?.writeText(createdLink.url);
              Success(t("Link copied"));
            }}
          />
        </Space.Compact>
        <Typography.Text type="secondary">
          {t("Valid until {{date}}", { date: formatDateTime(createdLink?.expiresAt, lng) })} ·{" "}
          {formatDay(report.date)}
        </Typography.Text>
      </Modal>
    </div>
  );
};

export default WorkReportEditor;
